import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { extractAllWithProgress, type ExtractedTripPlan } from '../../../api/import.api';
import { withinLimit } from '../../../utils/uploadLimits';

const MAX_FILES = 10;

/** The documents branch of the wizard: stage files, extract a plan from them. */
/** `agencyId`: ADR-0034 — staff importing for their agency charge its AI allowance. */
export function useDocumentImport(agencyId?: string) {
  const { t } = useTranslation();
  const [staged, setStaged] = useState<File[]>([]);
  const [plan, setPlan] = useState<ExtractedTripPlan | null>(null);
  const [language, setLanguage] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const [partial, setPartial] = useState<ExtractedTripPlan | null>(null);

  function addFiles(fileList: FileList | null) {
    if (!fileList || fileList.length === 0) return;
    const { accepted, errors } = withinLimit(Array.from(fileList), 'document');
    setError(errors[0] ?? null);
    if (accepted.length === 0) return;
    setStaged(prev => {
      const seen = new Set(prev.map(f => `${f.name}:${f.size}`));
      const merged = [...prev];
      for (const f of accepted) {
        const key = `${f.name}:${f.size}`;
        if (!seen.has(key)) { seen.add(key); merged.push(f); }
      }
      return merged.slice(0, MAX_FILES);
    });
  }

  function removeFile(index: number) {
    setStaged(prev => prev.filter((_, i) => i !== index));
  }

  function clear() {
    setPlan(null);
    setLanguage(null);
    setStaged([]);
    setPartial(null);
    setError(null);
  }

  async function analyze() {
    if (staged.length === 0 || importing) return;
    setError(null);
    setProgress({ current: 0, total: staged.length });
    setPartial(null);
    setImporting(true);
    try {
      const result = await extractAllWithProgress(staged, (processed, total, planSoFar) => {
        setProgress({ current: processed, total });
        setPartial(planSoFar);
      }, { agencyId });
      setPlan(result.plan);
      setLanguage(result.documentLanguage);
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 402
        ? t('dashboard.newTripWizard.importInsufficientCredits')
        : status === 429 ? t('import.errors.rateLimited')
        : t('dashboard.newTripWizard.importFailed'));
    } finally {
      setImporting(false);
    }
  }

  return { staged, plan, language, importing, error, progress, partial, addFiles, removeFile, clear, analyze };
}

export type DocumentImport = ReturnType<typeof useDocumentImport>;
